<template>
    <BaseModal :title="'Recycle Bin'" @close="closeModal" :visible="true" size="extra-large">
        <div class="rlms-table">
            <div class="rlms-table-inner">
                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Past Assessment</th>
                            <th>Completion Progress</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="list?.length > 0">
                            <tr v-for="(assessment) in list" :key="assessment.id">
                                <td>{{ formatDate(assessment.created_at) }}</td>
                                <td>Assessment #{{ assessment?.serialno }}</td>
                                <td>
                                    <AssessmentProgressBar :overallProgress="assessment?.stats?.total_percentage"
                                        :groups="assessment?.stats?.group_scores"/>
                                </td>
                                <td>
                                    <div class="rlms-flex rlms-items-center rlms-gap-20">
                                        <div class="rlms-cursor-pointer" @click="restoreAssessment(assessment)">
                                            <img class="restore-assessment" :src="rotateIcon">
                                        </div>
                                        <div class="rlms-cursor-pointer" @click="deleteAssessment(assessment)">
                                            <img class="delete-assessment" :src="redTrashIcon">
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr>
                                <td colspan="4">
                                    <div class="rlms-flex rlms-flex-col rlms-gap-30 rlms-text-center">
                                        <img :src="noAssessmentsImg" class="rlms-mx-auto"
                                            style="width: 100px">
                                        <p>No assessments have been moved to the recycle bin<br> for this learner.</p>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                    <tfoot>
                        <tr>
                            <th colspan="4">
                                <div>
                                    <paginate v-model="page" :page-count="totalPages" :margin-pages="2"
                                        :prev-text="'<'" :next-text="'>'" :container-class="'pagination'"
                                        :page-class="'page-item'" :active-class="'active-page'" v-if="totalItems > 0" />
                                </div>
                            </th>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </BaseModal>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import BaseModal from './BaseModal.vue';
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { formatDate } from '@/utils/format-helpers';
import confirmationIcon from '@/assets/images/confirmation.png';
import rotateIcon from '@/assets/images/rotate-left-icon2.png';
import redTrashIcon from '@/assets/images/red-trash-icon.png';
import noAssessmentsImg from '@/assets/images/no-assessments.png';
import { handleError } from '@/utils/handleError';
import paginate from 'vuejs-paginate-next';
import { showConfirmDialog, showDialog, showToast } from '@/utils/swal';
import AssessmentProgressBar from './assesments/AssessmentProgressBar.vue';
import { authrizedPermissions } from '@/utils/permissions';

// eslint-disable-next-line
const emit = defineEmits(['close', 'saved']);

// eslint-disable-next-line
const props = defineProps({
    organizationId: {
        type: Number,
        required: true,
    },
    learnerId: {
        type: Number,
        required: true,
    },
});

const showModal = ref(true);

const list = ref([]);
const learner = ref(null);
const page = ref(1);
const totalPages = ref(1);
const totalItems = ref(0);
const perPage = ref(10);

const closeModal = () => {
    showModal.value = false;
    emit('close');
};

const loader = useLoaderStore();

const fetchAssessments = async () => {
    try {
        loader.show();
        const { data } = await api.get(`/organizations/${props.organizationId}/assessments`, {
            params: {
                status: 0,
                learner_id: props.learnerId,
                page: page.value,
                per_page: perPage.value,
            }
        });

        list.value = data?.data?.assessments?.data || [];
        learner.value = data?.data?.learner || null;

        // Optional: store pagination meta if needed
        totalPages.value = data.data.assessments.last_page;
        totalItems.value = data.data.assessments.total;
    } catch (error) {
        closeModal();
        handleError(error);
    } finally {
        loader.hide();
    }
}

const init = async () => {
    page.value = 1;
    await fetchAssessments();
}

const restoreAssessment = async (assessment, status = 1) => {    
    if (!assessment?.can_edit) {
        let message = `You don’t have permission perform this action.`;

        if (learner.value?.is_administrator) {
            message = `You don’t have permission to restore this assessment because it was created before you were granted administrator access.`;
        } else if (learner.value?.is_authorized_user) {
            if (learner.value?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission to restore this assessment because it was created before you were granted group access.`;
            } else if (!learner.value?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `Assessment available to you through group access. You don’t have permission perform this action.`;
            }
        }

        showDialog({ icon: 'warning', html: message });
        return;
    }

    try {
        await api.put(`/organizations/${props.organizationId}/assessments/${assessment?.id}`, {
            status
        });

        showToast({
            icon: 'success',
            title: 'Restored! ',
            text: 'Assessment retored successfully!',
        });

        init();
        
        emit('saved');
    } catch (error) {
        handleError(error);
    }
}

const deleteAssessment = async (assessment) => {
    if (!assessment?.can_edit) {
        let message = `You don’t have permission perform this action.`;

        if (learner.value?.is_administrator) {
            message = `You don’t have permission to delete this assessment because it was created before you were granted administrator access.`;
        } else if (learner.value?.is_authorized_user) {
            if (learner.value?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission to delete this assessment because it was created before you were granted group access.`;
            } else if (!learner.value?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `Assessment available to you through group access. You don’t have permission perform this action.`;
            }
        }
        
        showDialog({ icon: 'warning', html: message });
        return;
    }

    const result = await showConfirmDialog({
        title: 'Are you sure?',
        html: '<p id="text-message">Are you sure?</p><span class="rlms-text-danger-500" id="delete-confirm"> This action will permanently remove and cannot be undone.</span>',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Confirm',
        imageUrl: confirmationIcon,
    });

    if (result.isConfirmed) {
        try {
            await api.delete(`/organizations/${props.organizationId}/assessments/${assessment?.id}`);

            showToast({
                icon: 'success',
                title: 'Deleted! ',
                text: 'Assessment has been deleted successfully!',
            });

            init();
        } catch (error) {
            handleError(error);
        }
    }
};

onMounted(() => {
    init();
});

watch(page, () => {
    fetchAssessments();
});
</script>
